import { useEffect, useRef, useState } from 'react';
import { ImageAtoms, CustomText } from '@vfit/shared/atoms';
import {
  getRedirectCoverage,
  IN_ACTIVATION,
  retrieveInTest,
  trackActionByActionType,
  usePage,
} from '@vfit/consumer/data-access';
import { useRedirectCoverageToolFlow } from '@vfit/consumer/hooks';
import { useQueryClient } from 'react-query';
import { useDeviceType } from '@vfit/shared/hooks';
import {
  getClickToCall,
  getVariantByActionStyle,
  linkInAppOpener,
  useTracking,
} from '@vfit/shared/data-access';
import { IActionSlide, IActionStyle, IActionType, IGradientImage } from '@vfit/shared/models';
import { Pills, VfModal } from '@vfit/shared/components';
import { fonts, purify } from '@vfit/shared/themes';
import { energyManager } from '@vfit/consumer/providers';
import { CoverageToolModal } from '@vfit/consumer/components';
import {
  ModalContent,
  ModalTitle,
  ProductCardBottom,
  ProductCardBottomContainer,
  ProductCardContainer,
  ProductCardTopImage,
  ProductCardTopImageContent,
  DivImgContainer,
  H3Header,
  H2Header,
  OptionListContainer,
} from './productCardLite.style';
import { IProductCardLiteProps } from './productCardLite.models';
import ProductCardLoader from './components/ProductCardLoader/productCardLoader';
import ProductCardTopContainer from './components/ProductCardTopContainer/productCardTopContainer';
import ProductMarketingLabel from './components/ProductMarketingLabel/productMarketingLabel';
import ProductOptions from './components/ProductOptions/productOptions';
import ProductCardCTAContainer from './components/ProductCardCTAContainer/productCardCTAContainer';
import { goDetail } from './productCardLite.utils';

const ProductCardLite = ({
  noMargin = false,
  isLoading = false,
  product: originalProduct,
  large,
  zoomIn,
  onActive,
  hideActions,
  setSpecHeight,
  cardIndex,
  haveMarketingLabel,
  isShoppingCart,
}: IProductCardLiteProps) => {
  const { page } = usePage();
  const specRef = useRef<HTMLDivElement | null>(null);
  const isInTest = retrieveInTest() && originalProduct?.enableAbTest;
  const abTestConfiguration = originalProduct.offerDetailABTest || {};
  const product = !isInTest
    ? originalProduct
    : {
        ...originalProduct,
        ...abTestConfiguration,
      };
  const {
    title,
    titleHtml,
    priceLiteLabel,
    image,
    price,
    originalPrice,
    recurrence,
    subprice,
    description,
    action,
    hubSecondAction,
    hubThirdAction,
    pills,
    slug,
    category,
    queryParamGoDetail,
    inflactionHub,
    inflactionCartView,
    cmsId,
    offerAllowance,
    aboveCtasHubLabel,
    hideMainAction,
  } = product;
  const [pDescription, pAboveCtasHubLabel, purifiedTitleHtml] = purify([
    description || '',
    aboveCtasHubLabel || '',
    titleHtml || title || '',
  ]);
  const [isShowCoverageTool, setIsShowCoverageTool] = useState(false);
  const [isInActivation] = useRedirectCoverageToolFlow(cmsId || '');
  const { isDesktop } = useDeviceType();
  const [modal, setModal] = useState({
    show: false,
    text: '',
    title: '',
  });
  const queryClient = useQueryClient();

  const { trackWithNewDatalayer } = useTracking({
    event: ['product_view'],
    disableInitialTrack: true,
  });

  useEffect(() => {
    if (!isShowCoverageTool && isInActivation) {
      setIsShowCoverageTool(true);
    }
  }, [isInActivation]);

  useEffect(() => {
    if (specRef?.current?.offsetHeight && setSpecHeight) {
      setSpecHeight(specRef.current?.offsetHeight);
    }
  }, [setSpecHeight, specRef]);

  const activeNow = () => {
    if (onActive) {
      onActive();
      return;
    }
    trackActionByActionType(
      {
        type: action?.type,
        title: action?.title,
      },
      product
    );
    switch (action?.type) {
      case IActionType.SHOW_DETAIL:
        goDetail(slug, queryParamGoDetail);
        break;
      case IActionType.CUSTOM_URL:
        if (action?.url) {
          linkInAppOpener(action.url, action?.isBlank === 'true' ? '_blank' : '_self');
        }
        break;
      case IActionType.CALL_ME_NOW:
        getClickToCall(queryClient, {
          product,
          idClickToCall: action?.key,
          clickToCallFallbackUrl: action?.callMeNow || action?.url,
        });
        break;
      case IActionType.CALL_ME_NOW_SUNRISE:
        if (!action?.title || !action?.url) return;
        getClickToCall(queryClient, {
          product,
          idClickToCall: action?.key,
          clickToCallFallbackUrl: action?.callMeNow || action?.url,
          clickToCallFallbackType: 'CTC_SUNRISE',
          clickToCallFallbackTemplate: action?.template,
        });
        break;
      case IActionType.COVERAGE_TOOL:
        setIsShowCoverageTool(true);
        break;
      case IActionType.CHECKOUT_MODAL_MOBILE:
        localStorage.setItem('tProd', JSON.stringify(product));
        window.location.href = getRedirectCoverage();
        break;
      case IActionType.COVERAGE_TOOL_WITH_REDIRECT:
        if (action?.url && product?.cmsId) {
          const alreadyHaveParams = action.url.includes('?');
          window.location.href = alreadyHaveParams
            ? `${action.url}&${IN_ACTIVATION}=${product?.cmsId || ''}`
            : `${action.url}?${IN_ACTIVATION}=${product?.cmsId || ''}`;
        }
        break;
      case IActionType.CLICK_TO_CALL_ENERGY:
        document.dispatchEvent(
          new CustomEvent('energyCtcNotificationEvent', {
            detail: {
              product,
              action,
              taggingOption: page?.params?.elements?.taggingOptions,
            },
          })
        );
        break;
      case IActionType.POST_ENRG:
        document.dispatchEvent(
          new CustomEvent('enrgServiceNotificationEvent', {
            detail: {
              nof: action?.localUrl,
            },
          })
        );
        break;
      case IActionType.ENERGY_FUNNEL:
        energyManager.handleEnergyManager({
          product,
          showModal: true,
        });
        break;
      default:
        break;
    }
  };

  const handleHubAction = (localAction?: IActionSlide) => {
    if (!localAction) return;
    const type = localAction?.type ? +localAction.type : -1;
    trackActionByActionType(
      {
        type,
        title: localAction?.title,
      },
      product
    );
    switch (type) {
      case IActionType.SHOW_DETAIL:
        goDetail(slug, queryParamGoDetail);
        break;
      case IActionType.CUSTOM_URL:
        if (localAction?.url)
          linkInAppOpener(localAction.url, localAction?.isBlank === 'true' ? '_blank' : '_self');
        break;
      case IActionType.CALL_ME_NOW:
        getClickToCall(queryClient, {
          product,
          idClickToCall: localAction?.key,
          clickToCallFallbackUrl: localAction?.callMeNow || localAction?.url,
        });
        break;
      case IActionType.CALL_ME_NOW_SUNRISE:
        if (!localAction?.title || !localAction?.url) return;
        getClickToCall(queryClient, {
          product,
          idClickToCall: localAction?.key,
          clickToCallFallbackUrl: localAction?.callMeNow || localAction?.url,
          clickToCallFallbackType: 'CTC_SUNRISE',
          clickToCallFallbackTemplate: localAction?.template,
        });
        break;
      case IActionType.CLICK_TO_CALL_ENERGY:
        document.dispatchEvent(
          new CustomEvent('energyCtcNotificationEvent', {
            detail: {
              product,
              action: localAction,
              taggingOption: page?.params?.elements?.taggingOptions,
            },
          })
        );
        break;
      case IActionType.POST_ENRG:
        document.dispatchEvent(
          new CustomEvent('enrgServiceNotificationEvent', {
            detail: {
              nof: localAction?.localUrl,
            },
          })
        );
        break;
      case IActionType.ENERGY_FUNNEL:
        energyManager.handleEnergyManager({
          product,
          showModal: true,
        });
        break;
      default:
        break;
    }
  };

  const hideCoverageTool = () => {
    const pageDatalayer: any = queryClient.getQueryData('pageDatalayer');
    if (pageDatalayer) {
      trackWithNewDatalayer({
        event: pageDatalayer?.event,
        event_label: pageDatalayer?.event_label,
        opts: pageDatalayer?.opts,
        pageProduct: pageDatalayer?.pageProduct,
        cartProduct: pageDatalayer?.cartProduct,
        visitorTrackingOpts: pageDatalayer?.visitorTrackingOpts,
      });
    }
    setIsShowCoverageTool(false);
  };

  const clickImage = () => {
    if (!product.disableClickDetail) goDetail(slug, queryParamGoDetail);
  };

  const showMarketing =
    ((product?.marketingLabel || product?.marketingSecondaryLabel) && !isDesktop) ||
    (haveMarketingLabel && isDesktop);

  return (
    <>
      {showMarketing && (
        <ProductMarketingLabel
          firstMarketingLabel={product?.marketingLabel}
          secondMarketingLabel={product?.marketingSecondaryLabel}
          firstMarketingLabelColor={product?.marketingLabelColor}
          secondMarketingLabelColor={product?.marketingSecondaryLabelColor}
          firstMarketingBackgroundColor={product?.marketingLabelBackgroundColor}
          secondMarketingBackgroundColor={product?.marketingSecondaryBackgroundColor}
        />
      )}
      <ProductCardContainer
        noMargin={noMargin}
        haveOfferLabel={haveMarketingLabel}
        showMarketing={showMarketing}
      >
        {isLoading && <ProductCardLoader />}
        {!isLoading && (
          <>
            {isDesktop && (
              <ProductCardTopImage
                large={large}
                isClickable={!product?.disableClickDetail}
                onClick={clickImage}
              >
                <Pills pills={pills || []} />
                <ProductCardTopImageContent>
                  <H2Header>{category}</H2Header>
                  <H3Header dangerouslySetInnerHTML={{ __html: purifiedTitleHtml || '' }} />
                </ProductCardTopImageContent>
                <DivImgContainer absolute zoomIn={zoomIn}>
                  <ImageAtoms
                    gradient={IGradientImage.GRADIENT_BOTTOM_40}
                    nameCard="topImage"
                    image={image}
                  />
                </DivImgContainer>
              </ProductCardTopImage>
            )}
            <ProductCardBottom haveBorderRadius={!isDesktop}>
              <ProductCardBottomContainer>
                <ProductCardTopContainer
                  offerAllowanceLabel={offerAllowance || []}
                  price={price}
                  recurrence={recurrence}
                  priceTitle={priceLiteLabel}
                  originalPrice={originalPrice}
                  subprice={subprice}
                />
                {pDescription && (
                  <div
                    className="productDescription"
                    dangerouslySetInnerHTML={{ __html: pDescription }}
                  />
                )}
                <OptionListContainer>
                  <ProductOptions
                    inflactions={isShoppingCart ? inflactionCartView : inflactionHub}
                    productPrice={product?.price}
                    productName={product?.title}
                    productId={product?.offerId?.toString()}
                    productCategory={product?.category || 'fixed'}
                  />
                </OptionListContainer>
                {!hideActions && (
                  <ProductCardCTAContainer
                    hideFirstButton={hideMainAction}
                    aboveText={pAboveCtasHubLabel}
                    firstButtonAction={activeNow}
                    firstButtonLabel={action?.title || ''}
                    firstButtonName={`action_card_${!cardIndex ? '1' : cardIndex + 1}_${
                      product?.offerId?.toString() || ''
                    }`}
                    secondButtonAction={() => handleHubAction(hubSecondAction)}
                    secondButtonLabel={hubSecondAction?.title || ''}
                    secondButtonName={`secondAction_card_${!cardIndex ? '1' : cardIndex + 1}_${
                      product?.offerId?.toString() || ''
                    }`}
                    secondButtonVariant={getVariantByActionStyle(
                      hubSecondAction?.style as unknown as IActionStyle
                    )}
                    thirdButtonAction={() => handleHubAction(hubThirdAction)}
                    thirdButtonLabel={hubThirdAction?.title || ''}
                    thirdButtonVariant={getVariantByActionStyle(
                      hubThirdAction?.style as unknown as IActionStyle
                    )}
                    thirdButtonName={`thirdAction_card_${!cardIndex ? '1' : cardIndex + 1}_${
                      product?.offerId?.toString() || ''
                    }`}
                  />
                )}
              </ProductCardBottomContainer>
            </ProductCardBottom>
          </>
        )}
        <VfModal
          height={563}
          isOpen={modal.show}
          handleClose={() => setModal({ show: false, text: '', title: '' })}
        >
          <>
            {modal.title && (
              <ModalTitle>
                <CustomText fontFamily={fonts.exbold} textAlign="left" text={modal.title} />
              </ModalTitle>
            )}
            <ModalContent>
              <CustomText size={18} lineHeight={24} textAlign="left" text={modal.text} />
            </ModalContent>
          </>
        </VfModal>
        <CoverageToolModal
          backgroundImage={product.image}
          isOpen={isShowCoverageTool}
          product={product}
          handleClose={hideCoverageTool}
        />
      </ProductCardContainer>
    </>
  );
};

export default ProductCardLite;

